// getMessageList.js
'use strict';
import { Collection } from 'immutable';
import createCachedSelector from '../resource/createCachedSelector.js';

import { getSelectedQueryOptionObject } from '../selector/getFeedQueryObject.js';
import getListData from '../selector/getListData.js';
import getMeData from '../selector/getMeData.js';
import getShowcaseListItems from '../selector/getShowcaseListItems.js';
import getMeStories from '../selector/getMeStories.js';
import getFeedName from '../selector/getFeedName.js';
import getSelectedFeedQueryOptions from '../selector/getSelectedFeedQueryOptions.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';

import {
  ProfileListType,
  PROFILE_FEED_REMOTE_CONFIG_KEY_MAP,
} from '../resource/profileConstants.js';
import { QueryPrefix, QueryType } from '../resource/feedConstants.js';

/**
 * Select message list by type
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} type - the list type.
 * @param {string[]} path - the list path.
 * @param {string} [dataKey = 'itemIds'] - data key.
 * @return {string[]} The selected message list.
 */
const getMessageList = createCachedSelector(
  (state, type, path, dataKey = 'itemIds') => {
    if (type === 'home' && path[2].startsWith('showcase')) {
      return getShowcaseListItems(state, path[2], dataKey);
    }
    if (type === 'discover') {
      return getListData(state, [...path, 'messages'], dataKey);
    }
    if (type === 'following') {
      return getListData(state, path, dataKey);
    }
    if (type === 'outbox') {
      const userId = path[2];
      const listType = path[1];
      const meId = getMeData(state, 'id');
      const isMe = meId && meId === userId;
      if (isMe && ProfileListType.STORY === listType) {
        return getMeStories(state, dataKey);
      }
      const remoteConfigKey = PROFILE_FEED_REMOTE_CONFIG_KEY_MAP[listType];
      const outboxFeedName = getFeedName(
        state,
        remoteConfigKey,
        '{USER_ID}',
        userId
      );
      const { feedNameWithQuery } = getSelectedQueryOptionObject(
        state,
        outboxFeedName,
        getSelectedFeedQueryOptions(state, remoteConfigKey, undefined),
        getRemoteConfigData(
          state,
          `${remoteConfigKey}_${QueryPrefix[QueryType.SORT]}`
        )?.[0]
      );
      const selectPath = ['home', 'messages', feedNameWithQuery];
      return getListData(state, selectPath, dataKey);
    }
    return getListData(state, path, dataKey);
  },
  result => {
    return result instanceof Collection ? result.toJS() : result;
  }
)((state, type, path, dataKey = 'itemIds') => `${type}:${path}:${dataKey}`);

export default getMessageList;
